import { sectionName, sectionOf, type Meal } from '../../../shared/meals'
import type { Cookbook } from '../cookbooks'

// A run of recipes for one meal, opening on its own page. The pages come from the server already in their sections.
export type Section = { meal: Meal | null; title: string; first: number; count: number }

// The sections of a cookbook divided by meal; none when it isn't, or when no recipe in it has been given a meal.
export function sectionsOf(cookbook: Pick<Cookbook, 'sections' | 'pages'>): Section[] {
  if (!cookbook.sections || !cookbook.pages.some((x) => x.recipe.meals.length)) return []
  const sections: Section[] = []
  for (const [i, page] of cookbook.pages.entries()) {
    const meal = sectionOf(page.recipe.meals)
    const last = sections.at(-1)
    if (last && last.meal === meal) last.count++
    else sections.push({ meal, title: sectionName(meal), first: i, count: 1 })
  }
  return sections
}

// Every stop in the book, in order: the cover, a dedication when there is one, the contents, then each recipe, with a
// section's opening page before its first recipe.
export type Leaf =
  | { kind: 'cover' }
  | { kind: 'dedication' }
  | { kind: 'contents' }
  | { kind: 'section'; section: Section; folio: number }
  | { kind: 'recipe'; index: number; folio: number }

// Page numbers count the way a book's do: the cover is page 1, then a dedication on page 3 when there is one (its back,
// page 2, left blank as in a printed book), the contents across the next spread, a section's opening page on the
// right with a blank page facing it, and each recipe across a spread, its picture on the left and the recipe on the
// right. A recipe's folio is its right-hand page.
export function leavesOf(cookbook: Pick<Cookbook, 'dedication' | 'sections' | 'pages'>): Leaf[] {
  const leaves: Leaf[] = [{ kind: 'cover' }]
  if (cookbook.dedication.trim()) leaves.push({ kind: 'dedication' })
  leaves.push({ kind: 'contents' })
  const sections = new Map(sectionsOf(cookbook).map((x) => [x.first, x]))
  let folio = contentsPageNumber(cookbook) + 1
  for (const index of cookbook.pages.keys()) {
    const section = sections.get(index)
    if (section) {
      folio += 2
      leaves.push({ kind: 'section', section, folio })
    }
    folio += 2
    leaves.push({ kind: 'recipe', index, folio })
  }
  return leaves
}

export const frontMatter = (cookbook: Pick<Cookbook, 'dedication'>) => (cookbook.dedication.trim() ? 2 : 0)
export const contentsPageNumber = (cookbook: Pick<Cookbook, 'dedication'>) => 2 + frontMatter(cookbook)

// The first picture in the book, for the cover's plate and the contents page.
export const firstPicture = (cookbook: Pick<Cookbook, 'pages'>) => cookbook.pages.find((x) => x.picture)?.picture

export const coverOf = (cookbook: Cookbook) => ({
  title: cookbook.title,
  subtitle: cookbook.subtitle,
  by: cookbook.by,
  picture: firstPicture(cookbook),
})

// The book a page at a time, as a phone reads it: each stop's pages in turn, a recipe's picture before its words (and
// only its words when it has no picture). Each page knows the stop it belongs to, so the reader's place in the address
// stays a stop.
export type OnePage = { leaf: number; kind: Leaf['kind'] | 'picture' }
export function onePagesOf(cookbook: Pick<Cookbook, 'dedication' | 'sections' | 'pages'>): OnePage[] {
  return leavesOf(cookbook).flatMap((leaf, n): OnePage[] =>
    leaf.kind === 'recipe' && cookbook.pages[leaf.index]!.picture
      ? [
          { leaf: n, kind: 'picture' },
          { leaf: n, kind: 'recipe' },
        ]
      : [{ leaf: n, kind: leaf.kind }],
  )
}
